import RestService from "../../../core/services/rest.service";
import type { AxiosProgressEvent } from "axios";
import type { FetchPageServiceParameters } from "~/core/composables/useFetchPageV2";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import type { TruststoreCertificate } from "~/modules/certificate/models/TruststoreCertificate.interface";
import type { TruststoreCertificateDetail } from "~/modules/certificate/models/TruststoreCertificateDetail.interface";
import type { TruststoreCertificateRequest } from "~/modules/certificate/models/TruststoreCertificateRequest.interface";
import type { UploadedCertificate } from "~/modules/certificate/models/UploadedCertificate.interface";

export class TruststoreService extends RestService {
  protected BASE_URL = "/truststore";
  protected UPLOAD_PATH = "/upload";
  /**
   * Get list of certificates in truststore
   * @param page
   * @param pageSize
   * @param search
   * @param sort
   * @return Promise of List of truststore certificates with context

   */
  getListOfCertificates({
    page,
    pageSize,
    searchParams,
    sort,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<TruststoreCertificate>
  > {
    const params = { page, pageSize, filter: searchParams, sort };
    return this.$api.get(this.BASE_URL, { params });
  }

  getCertificate(alias: string): Promise<TruststoreCertificateDetail> {
    return this.$api.get(`${this.BASE_URL}/${alias}`);
  }

  /**
   * Delete a certificate entry by alias
   * @param alias Identifier of the certificate
   */
  deleteCertificate(alias: string): Promise<void> {
    return this.$api.delete(`${this.BASE_URL}/${alias}`);
  }

  /**
   * Upload a certificate
   * @param certificate
   * @param progress
   */
  uploadCertificate(
    certificate: File,
    progress: Ref<number | undefined | null>,
  ): Promise<UploadedCertificate> {
    const formData = new FormData();
    formData.append("certificate", certificate, certificate.name);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        progress.value = progressEvent.total
          ? (progressEvent.loaded / progressEvent.total) * 100
          : 0;
      },
    };
    return this.$api.post(
      `${this.BASE_URL}${this.UPLOAD_PATH}`,
      formData,
      config,
    );
  }

  /**
   * Add a new certificate
   * @param certificate
   */
  addCertificate(certificate: TruststoreCertificateRequest): Promise<void> {
    return this.$api.post(`${this.BASE_URL}`, { certificate });
  }

  /**
   * Update a certificate
   * @param certificate
   */
  updateCertificate(certificate: TruststoreCertificateRequest): Promise<void> {
    return this.$api.put(`${this.BASE_URL}/${certificate.alias}`, {
      certificate,
    });
  }
}

export default new TruststoreService();
